import { useCallback, useEffect, useMemo } from "react";

import { Flex } from "@chakra-ui/react";
import { orderBy } from "lodash";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { TaskType } from "__graphql__/types";
import { PageName } from "analytics/events";
import { openOngoingActivityModal } from "app/stores/useLayoutStore";
import { routes } from "config/routes";
import { CheckScanner } from "flows/Inventory/shared/components/CheckScanner";
import { CheckListItem, ChecksList } from "flows/Inventory/shared/components/ChecksList";
import { ConnectionErrorModal } from "flows/Inventory/shared/components/ConnectionErrorModal";
import { TaskIsTakenModal } from "flows/Inventory/shared/components/TaskIsTakenModal";
import { useChecksList } from "flows/Inventory/shared/hooks/useChecksList";
import { SpinnerModal } from "shared/components/SpinnerModal";
import { SwipeablePageWrapper } from "shared/components/SwipeablePageWrapper";
import { useTimedActivityStore } from "shared/stores/useTimedActivityStore";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { isNotNullNorUndefined } from "utils/tsHelpers";

import { useBbdCheckStore } from "../stores/useBbdCheckStore";

export function BbdChecksList() {
  const intl = useIntl();
  const navigate = useNavigate();
  const resetBbdCheckStore = useBbdCheckStore((s) => s.reset);

  useEffect(() => {
    resetBbdCheckStore();
  }, [resetBbdCheckStore]);

  const timedActivityInstance = useTimedActivityStore((state) => state.timedActivityInstance);

  const {
    rawChecks,
    refetchChecks,
    isLoadingChecks,
    isLoadingFetchNextCheckAndStartCheck,
    startCheck,
    isErrorModalOpen,
    isTaskIsTakenModalOpen,
    onCloseErrorModal,
    onCloseTaskIsTakenModal,
  } = useChecksList({
    type: [TaskType.bbd_check],
    origin: PageName.INVENTORY_BBD_CHECK_LIST_PAGE,
  });

  const onSelectCheck = useCallback(
    (shelfId: string, priority: number) => {
      if (isNotNullNorUndefined(timedActivityInstance)) {
        openOngoingActivityModal();
        return;
      }
      startCheck(shelfId, priority);
    },
    [startCheck, timedActivityInstance],
  );

  const sortedAndGroupedChecks = useMemo<CheckListItem[]>(() => {
    // For now, we won't group bbd checks of the same shelf into one multiple checks, that feature may come later
    return orderBy(rawChecks, ["shelfNumber"], ["asc"]);
  }, [rawChecks]);

  return (
    <>
      <SwipeablePageWrapper
        refetch={refetchChecks}
        isFull
        justifyContent="flex-start"
        flexDirection="column"
        h="100%"
        isLoading={isLoadingChecks}
      >
        <NavigationHeader
          title={intl.formatMessage({
            id: "flows.inventory.bbd-checks.pages.bbd-checks-list.title",
          })}
          onClickGoBack={() => navigate(routes.inventory.root)}
        />
        <Flex h="100%" direction="column" w="100%" overflowY="scroll">
          <CheckScanner
            checksList={rawChecks}
            startCheck={startCheck}
            type={[TaskType.bbd_check]}
          />
          <ChecksList items={sortedAndGroupedChecks} onSelectCheck={onSelectCheck} />
        </Flex>
      </SwipeablePageWrapper>
      <TaskIsTakenModal
        isOpen={isTaskIsTakenModalOpen}
        onClose={onCloseTaskIsTakenModal}
        onRefresh={refetchChecks}
      />
      <ConnectionErrorModal
        isOpen={isErrorModalOpen}
        onClose={onCloseErrorModal}
        onRefresh={refetchChecks}
      />
      <SpinnerModal isOpen={isLoadingFetchNextCheckAndStartCheck} />
    </>
  );
}
